import { CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorResponse } from '../../../../shared/api/hscan';
import { KeycloakErrorResponseDetail } from '../../../../shared/api/hscan/account';
import { useChangePassword } from '../../../../shared/hooks/accountHook';
import { BlueRoundedButton } from '../../common/components/BlueRoundedButton';
import {
  EditInputContainer,
  EditInputField,
  EditInputLabel,
  EditInputText,
  EditTitle,
  ErrorMessage,
  MobileEditTitle,
} from '../../common/components/EditModal/styledComponents';
import { whiteCircularProgressStyle } from '../../common/styles';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

interface EditPasswordProps {
  onClose: () => void;
}
export const EditPassword = (props: EditPasswordProps) => {
  const { onClose } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.myPage.edit.password',
  });
  const { isPending, mutate: reset } = useChangePassword({
    onSuccess: () => onClose(),
    onError: (e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>) => {
      if (e.response?.status === 400) {
        setErrorMessage(`${t('errorMessage.wrongOldPassword')}`);
      } else if (e.response?.status === 500) {
        setErrorMessage(`${t('errorMessage.500')}`);
      } else {
        setErrorMessage(
          `${t(
            `errorMessage.${e.response?.data.code}`,
            t('errorMessage.unknown'),
          )}`,
        );
      }
    },
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const onSubmitNewPassword = useCallback(() => {
    if (
      oldPassword.length === 0 ||
      newPassword.length === 0 ||
      confirmPassword.length === 0
    ) {
      setErrorMessage(`${t('errorMessage.emptyPassword')}`);
    } else if (newPassword !== confirmPassword) {
      setErrorMessage(`${t('errorMessage.wrongNewPassword')}`);
    } else if (newPassword.length < 8) {
      setErrorMessage(`${t('errorMessage.passwordLength')}`);
    } else {
      reset({ oldPassword, newPassword });
    }
  }, [t, reset, oldPassword, newPassword, confirmPassword]);

  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  return (
    <>
      {isMobile ? (
        <MobileEditTitle>{`${t('title')}`}</MobileEditTitle>
      ) : (
        <EditTitle>{`${t('title')}`}</EditTitle>
      )}
      <EditInputField $marginTop={isMobile ? '64px' : '44px'}>
        <EditInputContainer>
          <EditInputLabel>{`${t('oldPassword.label')}`}</EditInputLabel>
          <EditInputText
            type="password"
            value={oldPassword}
            onChange={e => {
              setOldPassword(e.target.value);
            }}
            placeholder={`${t('oldPassword.placeholder')}`}
          />
        </EditInputContainer>
        <EditInputContainer>
          <EditInputLabel>{`${t('newPassword.label')}`}</EditInputLabel>
          <EditInputText
            type="password"
            value={newPassword}
            onChange={e => {
              setNewPassword(e.target.value);
            }}
            placeholder={`${t('newPassword.placeholder')}`}
          />
        </EditInputContainer>
        <EditInputContainer>
          <EditInputLabel $letterSpacing="-0.07em">
            {`${t('confirmPassword.label')}`}
          </EditInputLabel>
          <EditInputText
            type="password"
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(e.target.value);
            }}
            placeholder={`${t('confirmPassword.placeholder')}`}
          />
        </EditInputContainer>
      </EditInputField>
      <ErrorMessage>{errorMessage}</ErrorMessage>
      <BlueRoundedButton marginTop="69px" onClick={onSubmitNewPassword}>
        {isPending ? (
          <CircularProgress size={24} style={whiteCircularProgressStyle} />
        ) : (
          t('done')
        )}
      </BlueRoundedButton>
    </>
  );
};
